/**
 * TEMP-H-014/H-015: Secure WebSocket Hook with Polling Fallback
 * 
 * Integrates secure WebSocket bridge with polling fallback.
 * Automatically handles auth failures, reconnection, and graceful degradation.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  SecureWebSocketBridge,
  type SubscriptionManifest,
  type ConnectionStatus,
  type WebSocketBridgeConfig
} from '@/lib/websocket-bridge-ephemeral';
import { usePollingFallback } from '@/lib/polling-fallback';
import { useServerAuth } from './useAuth-cookie';
// HISI-FE-WS-01: Removed getCurrentBrand - using brandless WebSocket rooms
import {
  createInvalidationManager,
  type WebSocketInvalidationManager
} from '@/lib/websocket-invalidation';

export interface UseSecureWebSocketOptions {
  executionId: string;
  // HISI-FE-WS-01: Removed brand parameter - using brandless room model
  subscriptions: SubscriptionManifest;
  onConnectionChange?: (status: ConnectionStatus) => void;
  onAuthFailure?: (error: string) => void;
  enablePollingFallback?: boolean;
}

export interface WebSocketState {
  status: ConnectionStatus;
  isConnected: boolean;
  isPollingFallback: boolean;
  error: string | null;
  lastUpdate: Date | null;
}

/**
 * Secure WebSocket hook with authentication and polling fallback
 */
export function useSecureWebSocket(options: UseSecureWebSocketOptions) {
  const { user, isAuthenticated } = useServerAuth();
  const queryClient = useQueryClient();

  const [state, setState] = useState<WebSocketState>({
    status: 'disconnected',
    isConnected: false,
    isPollingFallback: false,
    error: null,
    lastUpdate: null
  });

  const bridgeRef = useRef<any>(null);
  const cleanupFunctionsRef = useRef<(() => void)[]>([]);
  const invalidationManagerRef = useRef<WebSocketInvalidationManager | null>(null);

  // Polling fallback hook
  const pollingFallback = usePollingFallback(
    options.executionId,
    useCallback((data: any) => {
      // Handle polling data updates
      handleDataUpdate(data, 'polling');
    }, [options.executionId]),
    useCallback((error: string) => {
      setState(prev => ({ ...prev, error }));
    }, []),
    user?.id // Pass user ID for server-side filtering/logging
  );

  /**
   * Handle data updates from WebSocket or polling
   */
  const handleDataUpdate = useCallback((data: any, source: 'websocket' | 'polling') => {
    setState(prev => ({
      ...prev,
      lastUpdate: new Date(),
      error: null
    }));

    // DV-TEMP-004: Use centralized invalidation manager
    const invalidationManager = invalidationManagerRef.current;
    if (!invalidationManager) {
      console.warn('Invalidation manager not initialized, falling back to legacy invalidation');
      // Legacy fallback - basic invalidation
      queryClient.invalidateQueries({ queryKey: ['timeline', options.executionId] });
      queryClient.invalidateQueries({ queryKey: ['execution', options.executionId] });
      return;
    }

    // Handle different data types using centralized invalidation
    if (data.events && Array.isArray(data.events)) {
      // Batch event updates
      invalidationManager.invalidateForEvent('batch_update', options.executionId, data);
    } else if (data.execution) {
      // Execution status updates
      invalidationManager.invalidateForEvent('processing', options.executionId, data);
    } else if (data.stage) {
      // Stage updates - determine event type from stage data
      const stage = data.stage;
      if (stage.status === 'completed') {
        invalidationManager.invalidateForEvent('stage_completed', options.executionId, data);
      } else if (stage.status === 'failed') {
        invalidationManager.invalidateForEvent('stage_failed', options.executionId, data);
      } else if (stage.status === 'started') {
        invalidationManager.invalidateForEvent('stage_started', options.executionId, data);
      } else {
        // Generic stage update
        invalidationManager.invalidateForEvent('processing', options.executionId, data);
      }
    } else {
      // Generic update - use processing event type
      invalidationManager.invalidateForEvent('processing', options.executionId, data);
    }

    // Log for debugging
    if (import.meta.env.DEV) {
      console.log(`📡 Centralized cache invalidation triggered by ${source} update for execution ${options.executionId}:`, {
        hasExecution: !!data.execution,
        hasEvents: !!data.events,
        hasStage: !!data.stage,
        eventsCount: data.events?.length || 0,
        stageStatus: data.stage?.status
      });
    }
  }, [options.executionId, queryClient]);

  /**
   * Initialize WebSocket connection
   */
  const initializeWebSocket = useCallback(async () => {
    if (!user || !isAuthenticated) {
      setState(prev => ({
        ...prev,
        status: 'disconnected',
        isConnected: false
      }));
      return;
    }

    try {
      setState(prev => ({ ...prev, status: 'connecting' }));

      // Initialize invalidation manager
      if (!invalidationManagerRef.current) {
        invalidationManagerRef.current = createInvalidationManager(queryClient);
      }

      // Create ephemeral WebSocket bridge
      const wsUrl = import.meta.env.VITE_WS_URL ||
                   import.meta.env.VITE_WEBSOCKET_URL ||
                   'https://api.hi-si.ai';

      const config: WebSocketBridgeConfig = {
        url: wsUrl,
        subscriptions: options.subscriptions,
        fallbackToPolling: options.enablePollingFallback ?? true,
        onAuthFailure: (error: string) => {
          console.error('🔒 WebSocket auth failure:', error);
          setState(prev => ({
            ...prev,
            status: 'auth_failed',
            isConnected: false,
            error
          }));

          // Start polling fallback
          if (options.enablePollingFallback !== false) {
            setState(prev => ({ ...prev, isPollingFallback: true }));
            pollingFallback.startPolling();
          }

          // Call user callback
          options.onAuthFailure?.(error);
        },
        onFallback: (reason: string) => {
          console.warn('🔄 Falling back to polling:', reason);
          setState(prev => ({
            ...prev,
            status: 'fallback',
            isPollingFallback: true
          }));
          pollingFallback.startPolling();
        }
      };

      const bridge = new SecureWebSocketBridge(config);

      bridgeRef.current = bridge;

      // Set up event listeners
      bridge.on('connection_status', (status) => {
        setState(prev => {
          const newState = {
            ...prev,
            status,
            isConnected: status === 'connected',
            isPollingFallback: status === 'fallback'
          };

          // Cancel polling when WebSocket successfully connects
          if (status === 'connected' && prev.isPollingFallback) {
            console.log('🔌 WebSocket connected, stopping polling fallback');
            pollingFallback.stopPolling();
          }

          return newState;
        });

        options.onConnectionChange?.(status);
      });

      bridge.on('auth_error', (error) => {
        setState(prev => ({ ...prev, error }));
      });

      bridge.on('pipeline_event', (event) => {
        // DV-TEMP-004: Use centralized invalidation for pipeline events
        const invalidationManager = invalidationManagerRef.current;
        if (invalidationManager && event.event_type) {
          invalidationManager.invalidateForEvent(event.event_type, options.executionId, event);
        } else {
          // Fallback to legacy handling
          handleDataUpdate({ events: [event] }, 'websocket');
        }
      });

      bridge.on('execution_update', (data) => {
        handleDataUpdate({ execution: data }, 'websocket');
      });

      bridge.on('stage_update', (data) => {
        handleDataUpdate({ stage: data }, 'websocket');
      });

      // DV-TEMP-004: Add specific timeline event handlers
      // These events are handled by the pipeline_event handler above
      // since the WebSocket bridge may not have these specific event types

      // Bridge cleanup is handled by bridge.disconnect()
      cleanupFunctionsRef.current = [];

      // Attempt connection
      await bridge.connect();

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'WebSocket connection failed';
      
      console.error('❌ WebSocket initialization failed:', errorMessage);
      setState(prev => ({ 
        ...prev, 
        status: 'auth_failed',
        isConnected: false,
        error: errorMessage 
      }));

      // Start polling fallback if enabled
      if (options.enablePollingFallback !== false) {
        setState(prev => ({ ...prev, isPollingFallback: true }));
        pollingFallback.startPolling();
      }
    }
  }, [user, isAuthenticated, options, handleDataUpdate, pollingFallback]);

  /**
   * Clean up connections
   */
  const cleanup = useCallback(() => {
    // Clean up event listeners first
    cleanupFunctionsRef.current.forEach(fn => fn());
    cleanupFunctionsRef.current = [];

    // Disconnect WebSocket bridge
    if (bridgeRef.current) {
      bridgeRef.current.disconnect();
      bridgeRef.current = null;
    }

    // Clean up invalidation manager
    if (invalidationManagerRef.current) {
      invalidationManagerRef.current.destroy();
      invalidationManagerRef.current = null;
    }

    // Stop polling
    pollingFallback.stopPolling();

    setState({
      status: 'disconnected',
      isConnected: false,
      isPollingFallback: false,
      error: null,
      lastUpdate: null
    });
  }, [pollingFallback]);

  /**
   * Manual reconnect with polling cancellation
   */
  const reconnect = useCallback(async () => {
    // Cancel polling immediately on reconnect attempt
    if (state.isPollingFallback) {
      console.log('🔄 Cancelling polling for WebSocket reconnect');
      pollingFallback.stopPolling();
      setState(prev => ({ ...prev, isPollingFallback: false }));
    }
    
    cleanup();
    await initializeWebSocket();
  }, [cleanup, initializeWebSocket, state.isPollingFallback, pollingFallback]);

  /**
   * Update subscriptions
   */
  const updateSubscriptions = useCallback((newSubscriptions: Partial<SubscriptionManifest>) => {
    if (bridgeRef.current) {
      bridgeRef.current.updateSubscriptions(newSubscriptions);
    }
  }, []);

  /**
   * Reauth with new token (no reconnect)
   */
  const reauth = useCallback(async (newToken: string): Promise<boolean> => {
    if (bridgeRef.current) {
      return await bridgeRef.current.reauth(newToken);
    }
    return false;
  }, []);

  // Initialize on mount and auth changes
  useEffect(() => {
    if (isAuthenticated) {
      initializeWebSocket();
    } else {
      cleanup();
    }

    return cleanup;
  }, [isAuthenticated]); // FIXED: Only depend on isAuthenticated, not the functions

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []); // FIXED: No dependencies needed for unmount cleanup

  /**
   * Force invalidation of all cache keys for the execution
   */
  const forceRefresh = useCallback(() => {
    if (invalidationManagerRef.current) {
      invalidationManagerRef.current.forceInvalidateExecution(options.executionId);
    } else {
      // Fallback: basic invalidation
      queryClient.invalidateQueries({ queryKey: ['timeline', options.executionId] });
      queryClient.invalidateQueries({ queryKey: ['execution', options.executionId] });
    }
  }, [options.executionId, queryClient]);

  return {
    ...state,
    reconnect,
    disconnect: cleanup,
    updateSubscriptions,
    reauth,
    forceRefresh, // DV-TEMP-004: Expose force refresh capability
    // Expose polling controls for advanced usage
    polling: {
      start: pollingFallback.startPolling,
      stop: pollingFallback.stopPolling,
      status: pollingFallback.getStatus,
      isActive: pollingFallback.isPolling
    },
    // Expose invalidation manager for debugging
    invalidation: invalidationManagerRef.current
  };
}